import React, { useState, useCallback } from "react";
import ModalAgendamento from "./ModalAgendamento";

// ─── Constantes ───────────────────────────────────────────────────────────────

const DIAS_KEY = [
  "domingo",
  "segunda",
  "terca",
  "quarta",
  "quinta",
  "sexta",
  "sabado",
];
const DIAS_ABREV = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const DIAS_FULL = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];
const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const HORAS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];

const PERIODOS = [
  {
    key: "manha",
    label: "Manhã",
    icone: "☀️",
    horas: [8, 9, 10, 11],
    cor: "var(--color-highlight)",
  },
  {
    key: "tarde",
    label: "Tarde",
    icone: "🌅",
    horas: [12, 13, 14, 15, 16, 17],
    cor: "var(--color-accent)",
  },
  {
    key: "noite",
    label: "Noite",
    icone: "🌙",
    horas: [18, 19, 20, 21],
    cor: "var(--color-primary)",
  },
];

const STATUS_CONFIG = {
  agendado:  { label: "Aguardando sua confirmação", blockCls: "bg-amber-100 text-amber-700",       chipCls: "bg-amber-50 text-amber-700" },
  pendente:  { label: "Pendente",                   blockCls: "bg-amber-100 text-amber-700",       chipCls: "bg-amber-50 text-amber-700" },
  confirmado:{ label: "Confirmado",                 blockCls: "bg-slate-blue-400 text-white",     chipCls: "bg-slate-blue-50 text-slate-blue-500" },
  recusado:  { label: "Recusado",                   blockCls: "bg-rose-400 text-white",           chipCls: "bg-rose-100 text-rose-700" },
  cancelado: { label: "Cancelado",                  blockCls: "bg-gray-400 text-white",           chipCls: "bg-gray-100 text-gray-600" },
  default:   { label: "Agendado",                   blockCls: "bg-amber-100 text-amber-700",       chipCls: "bg-amber-50 text-amber-700" },
};

// ─── Utilitários ──────────────────────────────────────────────────────────────

const dateKey = (year, month, day) =>
  `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const getPeriodo = (hora) => {
  if (hora >= 8 && hora < 12) return "manha";
  if (hora >= 12 && hora < 18) return "tarde";
  if (hora >= 18 && hora < 22) return "noite";
  return null;
};

const isDayAvail = (date, disponibilidade) => {
  const key = DIAS_KEY[date.getDay()];
  const d = disponibilidade?.[key];
  return d && (d.manha || d.tarde || d.noite);
};

const buildEventMap = (eventos = []) => {
  const map = {};
  eventos.forEach((ev) => {
    const start = new Date(ev.start);
    const end = new Date(ev.end);
    const key = dateKey(start.getFullYear(), start.getMonth(), start.getDate());
    if (!map[key]) map[key] = [];
    map[key].push({
      ...ev,
      startHour: start.getHours(),
      endHour: end.getHours(),
    });
  });
  return map;
};

const getStatusConfig = (status) =>
  STATUS_CONFIG[status] || STATUS_CONFIG.default;

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function Toolbar({
  label,
  onPrev,
  onNext,
  onToday,
  view,
  onChangeView,
  showBack,
  onBack,
}) {
  const btnBase =
    "text-xs font-medium text-slate-blue-400 bg-slate-blue-50 border border-[#c7caec] rounded-lg px-3 py-[5px] cursor-pointer transition-colors leading-[1.4] hover:bg-slate-blue-400 hover:text-white";
  const btnActive = "bg-slate-blue-400 text-white border-slate-blue-400";

  return (
    <div className="flex flex-wrap items-center gap-2.5 border-b border-warm-200 bg-white px-[18px] py-3">
      {showBack && (
        <button
          className={`flex items-center gap-1.5 ${btnBase}`}
          onClick={onBack}
        >
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M13 4l-6 6 6 6" />
          </svg>
          Mês
        </button>
      )}

      <div className="flex gap-1">
        <button className={btnBase} onClick={onPrev}>‹</button>
        <button className={btnBase} onClick={onToday}>Hoje</button>
        <button className={btnBase} onClick={onNext}>›</button>
      </div>

      <span className="flex-1 text-center text-[15px] font-semibold text-warm-800">{label}</span>

      <div className="flex gap-1">
        {["mes", "semana", "dia"].map((v) => (
          <button
            key={v}
            className={`${btnBase} ${view === v ? btnActive : ""}`}
            onClick={() => onChangeView(v)}
          >
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
}

function LegendBar({ saldoHoras }) {
  const leg = (color, label) => (
    <div className="flex items-center gap-1.5 text-[11px] text-warm-400">
      <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />
      {label}
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-3.5 border-b border-warm-200 bg-warm-50 px-[18px] py-[9px]">
      <div className="flex items-center gap-1.5 text-[11px] text-warm-400">
        <span className="inline-block h-2 w-2 shrink-0 rounded-full border-[1.5px] border-warm-300 bg-white" /> Disponível
      </div>
      <div className="flex items-center gap-1.5 text-[11px] text-warm-400">
        <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px] border border-warm-200 bg-warm-50" /> Bloqueado
      </div>
      {leg("var(--color-primary)", "Confirmado")}
      {leg("var(--color-highlight)", "Pendente")}
      {leg("var(--color-accent)", "Recusado")}
      {leg("var(--color-success)", "Agendado")}
      {saldoHoras != null && (
        <div className="ml-auto text-xs text-warm-400">
          Saldo: <strong className="text-slate-blue-400">{saldoHoras}h</strong>
        </div>
      )}
    </div>
  );
}

// ─── Vista Mês ────────────────────────────────────────────────────────────────

function MonthView({
  year,
  month,
  disponibilidade,
  eventMap,
  onSelectDay,
  onSelectEvent,
  user,
}) {
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay();
  const endFill = (7 - ((lastDay.getDay() + 1) % 7)) % 7;

  const cells = [];

  // Dias do mês anterior
  for (let i = 0; i < startDow; i++) {
    const d = new Date(year, month, 1 - startDow + i);
    cells.push({ date: d, currentMonth: false });
  }
  // Dias do mês
  for (let d = 1; d <= lastDay.getDate(); d++) {
    cells.push({ date: new Date(year, month, d), currentMonth: true });
  }
  // Dias do mês seguinte
  for (let i = 1; i <= endFill; i++) {
    cells.push({ date: new Date(year, month + 1, i), currentMonth: false });
  }

  return (
    <div className="p-4">
      <div className="grid grid-cols-7 overflow-hidden rounded-xl border border-warm-200">
        {DIAS_ABREV.map((d) => (
          <div
            key={d}
            className="border-b border-r border-warm-200 bg-warm-50 px-1 py-2 text-center text-[11px] font-semibold text-warm-400 last:border-r-0"
          >
            {d}
          </div>
        ))}

        {cells.map(({ date, currentMonth }, idx) => {
          const isBaba = user?.tipo === "baba";
          const isPast = date < todayStart;
          const avail = currentMonth && isDayAvail(date, disponibilidade);
          const canClick = currentMonth && !isPast && (avail || isBaba);

          const isToday =
            today.getFullYear() === date.getFullYear() &&
            today.getMonth() === date.getMonth() &&
            today.getDate() === date.getDate();
          const key = dateKey(date.getFullYear(), date.getMonth(), date.getDate());
          const evs = eventMap[key] || [];

          const cellBase = "min-h-[47px] overflow-hidden border-b border-r border-warm-200 p-[7px_6px_6px] relative transition-colors [&:nth-child(7n)]:border-r-0";
          const cellState = !currentMonth
            ? "cursor-default bg-warm-50 opacity-45"
            : avail && !isPast
            ? "cursor-pointer bg-white hover:bg-slate-blue-50"
            : "cursor-default bg-warm-50";

          return (
            <div
              key={idx}
              className={`${cellBase} ${cellState}`}
              style={{ cursor: canClick ? "pointer" : "default" }}
              onClick={() => canClick && onSelectDay(date)}
            >
              <span
                className={[
                  "inline-flex h-6 w-6 items-center justify-center rounded-full text-[13px] font-semibold leading-none transition-colors",
                  isToday
                    ? "bg-slate-blue-400 text-white"
                    : avail && !isPast
                    ? "text-warm-800 hover:bg-[#c7caec]"
                    : "text-warm-400",
                ].join(" ")}
              >
                {date.getDate()}
              </span>

              {currentMonth && (!avail || isPast) && (
                <span className="mt-0.5 block text-[9px] font-medium uppercase tracking-[0.04em] text-warm-300" />
              )}

              {evs.slice(0, 2).map((ev, i) => {
                const { chipCls } = getStatusConfig(ev.status);
                return (
                  <div
                    key={i}
                    className={`mt-0.5 block overflow-hidden text-ellipsis whitespace-nowrap rounded-[5px] px-1.5 py-0.5 text-[10px] font-medium ${chipCls}`}
                    onClick={(e) => { e.stopPropagation(); onSelectEvent?.(ev); }}
                  >
                    {ev.title || "Agendamento"}
                  </div>
                );
              })}

              {evs.length > 2 && (
                <div className="mt-0.5 pl-0.5 text-[10px] text-warm-400">
                  +{evs.length - 2}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Vista Semana (Linha única) ─────────────────────────────────────────────

function WeekView({
  date,
  disponibilidade,
  eventMap,
  onSelectDay,
  onSelectEvent,
  user,
}) {
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  // Encontra o domingo desta semana para iniciar a linha
  const startOfWeek = new Date(date);
  startOfWeek.setDate(date.getDate() - date.getDay());

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    days.push(d);
  }

  return (
    <div className="p-4">
      <div className="grid grid-cols-7 overflow-hidden rounded-xl border border-warm-200">
        {DIAS_ABREV.map((d) => (
          <div
            key={d}
            className="border-b border-r border-warm-200 bg-warm-50 px-1 py-2 text-center text-[11px] font-semibold text-warm-400 last:border-r-0"
          >
            {d}
          </div>
        ))}

        {days.map((d, idx) => {
          const isBaba = user?.tipo === "baba";
          const avail = isDayAvail(d, disponibilidade);
          const isToday =
            today.getFullYear() === d.getFullYear() &&
            today.getMonth() === d.getMonth() &&
            today.getDate() === d.getDate();
          const isPast = d < todayStart;
          const canClick = !isPast && (avail || isBaba);

          const key = dateKey(d.getFullYear(), d.getMonth(), d.getDate());
          const evs = eventMap[key] || [];
          const cellBase = "min-h-[47px] overflow-hidden border-b border-r border-warm-200 p-[7px_6px_6px] relative transition-colors [&:nth-child(7n)]:border-r-0";
          const cellState = avail && !isPast
            ? "cursor-pointer bg-white hover:bg-slate-blue-50"
            : "cursor-default bg-warm-50";

          return (
            <div
              key={idx}
              className={`${cellBase} ${cellState}`}
              style={{ cursor: canClick ? "pointer" : "default" }}
              onClick={() => canClick && onSelectDay(d)}
            >
              <span
                className={[
                  "inline-flex h-6 w-6 items-center justify-center rounded-full text-[13px] font-semibold leading-none transition-colors",
                  isToday
                    ? "bg-slate-blue-400 text-white"
                    : avail && !isPast
                    ? "text-warm-800 hover:bg-[#c7caec]"
                    : "text-warm-400",
                ].join(" ")}
              >
                {d.getDate()}
              </span>
              {(!avail || isPast) && (
                <span className="mt-0.5 block text-[9px] font-medium uppercase tracking-[0.04em] text-warm-300">
                  Indisponível
                </span>
              )}
              {evs.slice(0, 2).map((ev, i) => {
                const { chipCls } = getStatusConfig(ev.status);
                return (
                  <div
                    key={i}
                    className={`mt-0.5 block overflow-hidden text-ellipsis whitespace-nowrap rounded-[5px] px-1.5 py-0.5 text-[10px] font-medium ${chipCls}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectEvent?.(ev);
                    }}
                  >
                    {ev.title || "Agendamento"}
                  </div>
                );
              })}
              {evs.length > 2 && (
                <div className="mt-0.5 pl-0.5 text-[10px] text-warm-400">+{evs.length - 2}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Vista Dia ────────────────────────────────────────────────────────────────

function DayView({
  date,
  disponibilidade,
  eventMap,
  onSelectSlot,
  onSelectEvent,
  user,
}) {
  const key = dateKey(date.getFullYear(), date.getMonth(), date.getDate());
  const evs = eventMap[key] || [];

  // Mapeia hora → evento (para checar span/continuação)
  const evByHour = {};
  evs.forEach((ev) => {
    for (let h = ev.startHour; h < ev.endHour; h++) {
      evByHour[h] = ev;
    }
  });

  const diaKey = DIAS_KEY[date.getDay()];
  const disp = disponibilidade?.[diaKey] || {};

  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const isPastDay = date < todayStart;

  const handleBlockDay = () => {
    const start = new Date(date);
    start.setHours(8, 0, 0, 0);
    const end = new Date(date);
    end.setHours(22, 0, 0, 0);
    onSelectSlot({ start, end, isFullDay: true });
  };

  return (
    <div className="flex flex-col">
      {/* Cabeçalho do dia */}
      <div className="flex items-center gap-3 border-b border-warm-200 px-5 py-3.5">
        <div className="text-[40px] font-bold leading-none text-slate-blue-400">
          {date.getDate()}
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-[14px] font-semibold text-warm-800">{DIAS_FULL[date.getDay()]}</span>
          <span className="text-xs text-warm-400">
            {MESES[date.getMonth()]} {date.getFullYear()}
          </span>
        </div>
        {user?.tipo === "baba" && (
          <button
            className="ml-auto rounded-lg border border-warm-200 px-3 py-1.5 text-xs font-medium text-warm-600 hover:bg-warm-100 transition-colors"
            onClick={handleBlockDay}
          >
            Bloquear Dia
          </button>
        )}
      </div>

      {/* Scroll de horas */}
      <div className="max-h-[480px] overflow-y-auto">
        {PERIODOS.map((periodo) => {
          const disponivel = disp[periodo.key];
          if (!disponivel) return null;

          return (
            <React.Fragment key={periodo.key}>
              {/* Divisor de período */}
              <div className="sticky top-0 z-[2] flex items-center gap-1.5 border-b border-warm-200 bg-warm-50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-warm-400">
                <span
                  className="inline-block h-[7px] w-[7px] shrink-0 rounded-full"
                  style={{ background: periodo.cor }}
                />
                {periodo.icone} {periodo.label}
              </div>

              {periodo.horas.map((hora) => {
                const ev = evByHour[hora];
                const isContinuation = ev && ev.startHour < hora;
                if (isContinuation) return null;

                const isStart = ev && ev.startHour === hora;
                const slotSpan = isStart ? ev.endHour - ev.startHour : 1;
                const { blockCls, label: statusLabel } = isStart
                  ? getStatusConfig(ev.status)
                  : { blockCls: "", label: "" };

                const handleClick = () => {
                  if (!disponivel || isPastDay) return;
                  if (isStart) { onSelectEvent?.(ev); return; }
                  const start = new Date(date);
                  start.setHours(hora, 0, 0, 0);
                  const end = new Date(date);
                  end.setHours(hora + 1, 0, 0, 0);
                  onSelectSlot({ start, end });
                };

                const rowBase = "grid border-b border-warm-200 last:border-b-0 transition-colors";
                const rowState = !disponivel || isPastDay
                  ? "cursor-not-allowed bg-warm-50"
                  : isStart
                  ? "cursor-default bg-white"
                  : "group cursor-pointer bg-white hover:bg-slate-blue-50";

                return (
                  <div
                    key={hora}
                    className={`${rowBase} ${rowState}`}
                    style={{ gridTemplateColumns: "52px 1fr", minHeight: `${slotSpan * 52}px` }}
                    onClick={handleClick}
                  >
                    {/* Coluna de hora */}
                    <div className="border-r border-warm-200 pt-1.5 pr-2.5 pl-3 text-right text-[10px] whitespace-nowrap text-warm-400">
                      {String(hora).padStart(2, "0")}:00
                    </div>
                    {/* Coluna de slot */}
                    <div className="flex items-start px-3 py-1.5">
                      {isStart ? (
                        <div className={`flex w-full flex-col gap-0.5 rounded-[9px] px-3 py-1.5 ${blockCls}`}>
                          <span className="text-[13px] font-semibold leading-[1.2]">
                            {ev.title || "Agendamento"}
                          </span>
                          <span className="text-[11px] opacity-80">
                            {statusLabel} · {slotSpan}h
                          </span>
                        </div>
                      ) : disponivel ? (
                        <span className="self-center text-xs font-medium text-[#c7caec] transition-colors group-hover:text-slate-blue-400">
                          + Agendar horário
                        </span>
                      ) : (
                        <span className="self-center text-[11px] font-medium text-warm-300">
                          Horário bloqueado
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function CalendarioAgenda({
  eventos = [],
  babaId,
  disponibilidade = {},
  onSelectEvent,
  onSelectSlot,
  onSelectDay,
  user,
  vendaSelecionada,
  fetchEventos,
  saldoHoras,
  valorHora,
  nomeBaba,
}) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [view, setView] = useState("mes");
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [slotSelecionado, setSlotSelecionado] = useState(null);

  const eventMap = buildEventMap(eventos);

  // ── Navegação ──────────────────────────────────────────────────────────────

  const handlePrev = useCallback(() => {
    if (view === "mes") {
      if (month === 0) {
        setMonth(11);
        setYear((y) => y - 1);
      } else setMonth((m) => m - 1);
    } else if (view === "semana" && selectedDate) {
      const d = new Date(selectedDate);
      d.setDate(d.getDate() - 7);
      setSelectedDate(d);
      setYear(d.getFullYear());
      setMonth(d.getMonth());
    } else if (view === "dia" && selectedDate) {
      const d = new Date(selectedDate);
      d.setDate(d.getDate() - 1);
      setSelectedDate(d);
      setYear(d.getFullYear());
      setMonth(d.getMonth());
    }
  }, [view, month, selectedDate]);

  const handleNext = useCallback(() => {
    if (view === "mes") {
      if (month === 11) {
        setMonth(0);
        setYear((y) => y + 1);
      } else setMonth((m) => m + 1);
    } else if (view === "semana" && selectedDate) {
      const d = new Date(selectedDate);
      d.setDate(d.getDate() + 7);
      setSelectedDate(d);
      setYear(d.getFullYear());
      setMonth(d.getMonth());
    } else if (view === "dia" && selectedDate) {
      const d = new Date(selectedDate);
      d.setDate(d.getDate() + 1);
      setSelectedDate(d);
      setYear(d.getFullYear());
      setMonth(d.getMonth());
    }
  }, [view, month, selectedDate]);

  const handleToday = useCallback(() => {
    setYear(today.getFullYear());
    setMonth(today.getMonth());
    setView("mes");
    setSelectedDate(null);
  }, []);

  const handleChangeView = useCallback(
    (v) => {
      setView(v);
      if (v !== "mes" && !selectedDate) {
        setSelectedDate(new Date(today));
      }
    },
    [selectedDate, today],
  );

  const handleSelectDay = useCallback(
    (date) => {
      if (user?.tipo === "baba") {
        const key = dateKey(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
        );
        const dayEvents = eventMap[key] || [];
        // Verifica se já existe um bloqueio manual de dia inteiro
        const fullBlock = dayEvents.find(
          (ev) =>
            ev.familia_id === ev.baba_id &&
            ev.startHour === 8 &&
            ev.endHour === 22,
        );

        // Se houver prop onSelectDay (como no DashboardBabysitter), chamamos ela para abrir o popup
        // Se não houver, ou se for para mudar de vista, prosseguimos
        if (onSelectDay) {
          onSelectDay(date);
          // Se quisermos que o popup abra mas também mude a vista, não damos return
          return;
        }
      }

      setSelectedDate(date);
      setYear(date.getFullYear());
      setMonth(date.getMonth());
      setView("dia");
    },
    [user, eventMap, onSelectEvent, onSelectSlot],
  );

  const handleSelectSlot = useCallback(({ start, end }) => {
    setSlotSelecionado({ start, end });
    setModalAberto(true);
  }, []);

  const handleBack = useCallback(() => {
    setView("mes");
    setSelectedDate(null);
  }, []);

  // ── Label da toolbar ───────────────────────────────────────────────────────

  const toolbarLabel =
    view === "dia" && selectedDate
      ? `${DIAS_FULL[selectedDate.getDay()]}, ${selectedDate.getDate()} de ${MESES[selectedDate.getMonth()]}`
      : view === "semana" && selectedDate
        ? `Semana de ${selectedDate.getDate()} de ${MESES[selectedDate.getMonth()]}`
        : `${MESES[month]} ${year}`;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="overflow-hidden rounded-2xl border border-warm-200 bg-white font-body text-warm-800">
      <Toolbar
        label={toolbarLabel}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
        view={view}
        onChangeView={handleChangeView}
        showBack={view !== "mes"}
        onBack={handleBack}
      />

      <LegendBar saldoHoras={saldoHoras} />

      {view === "mes" && (
        <MonthView
          year={year}
          month={month}
          disponibilidade={disponibilidade}
          eventMap={eventMap}
          onSelectDay={handleSelectDay}
          onSelectEvent={onSelectEvent}
          user={user}
        />
      )}

      {view === "semana" && selectedDate && (
        <WeekView
          date={selectedDate}
          disponibilidade={disponibilidade}
          eventMap={eventMap}
          onSelectDay={handleSelectDay}
          onSelectEvent={onSelectEvent}
          user={user}
        />
      )}

      {view === "dia" && selectedDate && (
        <DayView
          date={selectedDate}
          disponibilidade={disponibilidade}
          eventMap={eventMap}
          onSelectSlot={onSelectSlot || handleSelectSlot}
          onSelectEvent={onSelectEvent}
          user={user}
        />
      )}

      <ModalAgendamento
        aberto={modalAberto}
        onClose={() => setModalAberto(false)}
        slot={slotSelecionado}
        user={user}
        babaId={babaId}
        vendaSelecionada={vendaSelecionada}
        saldoHoras={saldoHoras}
        valorHora={valorHora}
        nomeBaba={nomeBaba}
        onSuccess={() => {
          setModalAberto(false);
          fetchEventos?.();
        }}
      />
    </div>
  );
}
